import React, { useEffect, useState } from "react";
import dayjs from "dayjs";

import { useMutation, useQuery } from "@tanstack/react-query";

import { Button, Col, Collapse, Divider, Flex, Form, FormInstance, Input, notification, Row, Select, Spin, Tooltip, Typography } from "antd";
import { useForm, useWatch } from "antd/lib/form/Form";
import { DownOutlined, PlusOutlined } from "@ant-design/icons";

import { AlunoService } from "../../../../../Services/Domain/Pessoa/AlunoService";
import { CadastroDeResponsavel } from "./CadastroDeResponsavel";
import { EnumTipoDeDocumento } from "../../../../../Models/Domain/Pessoa/Enum";
import { Formatacoes } from "../../../../../Utils/Formatacao";
import { InputComReactIMask } from "../../../../../Components/InputComReactIMask";
import { Ordenar } from "../../../../../Utils/Ordenacao";
import { PropriedadesDeNotificacao } from "../../../../../Utils/Notificacoes";
import { ResponsavelService } from "../../../../../Services/Domain/Pessoa/ResponsavelService";
import { SelectComPesquisa } from "../../../../../Components/SelectComPesquisa";
import { TipoDeDocumentoPrincipal, TypeAluno } from "../../../../../Models/Domain/Pessoa/Aluno";
import { TypeResponsavel } from "../../../../../Models/Domain/Pessoa/Responsavel";

import { TypeAlunoENacionalidade } from "../../../../../Models/Domain/Pessoa/AlunoENacionalidade";
import WebHttpErroResponse from "../../../../../Models/Utils/Error/WebHttpErroResponse";
import VinculoNovoResponsavelModal from "../../../../../AreaRestritta/Components/Manager/Pessoa/AlunoResponsavel/VinculoNovoResponsavelModal";
import InputDate from "@/Components/Base/Input/InputDate";
import PessoaGeneroSelect from "@/Components/Populados/Pessoa/PessoaGenero/PessoaGeneroSelect";

export interface IFormularioDeCadastroDeAluno {
    aluno: TypeAluno,
    responsavel: TypeResponsavel,
    nacionalidades: TypeAlunoENacionalidade[],

    meta: {
        tipoDeDocumentoPrincipal: TipoDeDocumentoPrincipal
    }
}

export const CadastroDeAluno: React.FC<
    {
        aoCancelar?: () => any,
        depoisDeFinalizar?: (aluno?: TypeAluno) => any,
        txtSalvar?: string,
        txtAtualizar?: string,

        limparFormularioAoFinalizar?: boolean,
        notificar?: boolean,

        formParam?:[FormInstance<IFormularioDeCadastroDeAluno>],

        /**
         * @default true
         */
        persistirDados?:boolean,
        /**
         * Metodo só será utilizado caso {persistirDados} seja {false}
         * @param valores 
         * @returns 
         */
        onFinish?:(valores: TypeAluno) => void
    } &
    (
        { privilegio: "administrador", permitirCadastroDeResponsavel?: boolean, alunoEmAlteracao?: TypeAluno } |
        { privilegio: "responsavel", alunoEmAlteracao?: undefined } |
        { privilegio: "responsavel", alunoEmAlteracao: TypeAluno } 
    )
> = ({
    persistirDados,
    formParam,
    onFinish,
    ...props
}) => {
    persistirDados = persistirDados ?? true;

    const estaAlterando = (props.alunoEmAlteracao?.codigo ?? 0) > 0;
    
    const [ novoForm ] = useForm<IFormularioDeCadastroDeAluno>()
    const form = formParam?.[0] ?? novoForm;
    const valoresDoFormulario = useWatch([], form);
    const generoForm = useWatch(["aluno", "pessoa", "codGenero"], form);

    const [nacionalidades, setNacionalidades] = useState<TypeAlunoENacionalidade[]>(props.alunoEmAlteracao?.nacionalidades ?? []);

    const responsaveisDisponiveis = useQuery({
        enabled: props.privilegio == "administrador",
        queryKey: ["listarResponsaveis"],
        queryFn: () => new ResponsavelService().listar({ativo: true}),
    })

    const [configurouAsDependenciasDeAtualizacao, setConfigurouAsDependenciasDeAtualizacao] = useState<boolean>(false);
    useEffect(() => {
        if (props.alunoEmAlteracao == undefined) return;

        const responsavel = (
            props.alunoEmAlteracao.responsaveis?.find((responsavel) => responsavel.principal == true )?.responsavel ??
            props.alunoEmAlteracao.responsaveis?.at(0)?.responsavel
        )

        form.resetFields();
        form.setFieldsValue({
            aluno: {
                ...props.alunoEmAlteracao,
                dataDeExpedicaoDoDocumento: props.alunoEmAlteracao.dataDeExpedicaoDoDocumento ? dayjs(Formatacoes.paraData(props.alunoEmAlteracao.dataDeExpedicaoDoDocumento)) : undefined,
                dataDeNascimento: props.alunoEmAlteracao.dataDeNascimento ? dayjs(Formatacoes.paraData(props.alunoEmAlteracao.dataDeNascimento)) : undefined,
                dataDeCriacao: props.alunoEmAlteracao.dataDeCriacao ? dayjs(Formatacoes.paraData(props.alunoEmAlteracao.dataDeCriacao)) : undefined
             },
             responsavel: {
                ...responsavel
             },
             meta: {
                tipoDeDocumentoPrincipal: props.alunoEmAlteracao?.pessoa?.cpf ? "cpf" : props.alunoEmAlteracao.pessoa?.rne? "rne" : "passaporte"
             }
        })

        setNacionalidades(props.alunoEmAlteracao.nacionalidades ?? [])

        setConfigurouAsDependenciasDeAtualizacao(true);
    }, [props.alunoEmAlteracao])

    const inserirAluno = useMutation({
        mutationFn: async (payload: TypeAluno) => {
            // if (props.privilegio == 'responsavel' && props.alunoEmAlteracao == undefined) {
            //     return new AreaRestritaUsuarioService().cadastrarAlunoParaResponsavel({ aluno: payload, senhaDaViagem: props.senhaDaViagem })
            // }

            if (props.privilegio == 'responsavel' || !persistirDados) throw new WebHttpErroResponse({
                statusCode : 403,
                status : "Forbidden",
                mensagem : "Você não tem permissão para realizar essa operação.",
                data : null
            } as any);

            return new AlunoService().inserir(payload);
        },
        onSuccess: (response) => {
            if(props.notificar !== false){
                let mensagem: JSX.Element = <> Cadastro realizado com sucesso. </>
                notification.open({
                    ...PropriedadesDeNotificacao.Sucesso(mensagem)
                })
            }

            if(props.limparFormularioAoFinalizar !== false){
                form.resetFields();
            }
            
            const alunoinserido: TypeAluno = response.data;
            props.depoisDeFinalizar?.(alunoinserido);
        }
    });

    const atualizarAluno = useMutation({
        mutationFn: async (payload: TypeAluno) => {
            if (!persistirDados) throw new WebHttpErroResponse({
                statusCode : 403,
                status : "Forbidden",
                mensagem : "Você não tem permissão para realizar essa operação.",
                data : null
            } as any);

            return new AlunoService().atualizar(payload)
        },
        onSuccess: () => {
            if (props.notificar !== false) {
                let mensagem: JSX.Element = <> Cadastro atualizado com sucesso. </>
                notification.open({
                    ...PropriedadesDeNotificacao.Sucesso(mensagem)
                })
            }

            if(props.limparFormularioAoFinalizar !== false){
                form.resetFields();
            }

            props.depoisDeFinalizar?.();
        }
    })
    
    const aoFinalizar = (valores: IFormularioDeCadastroDeAluno) => {
        const estaEditando = props.alunoEmAlteracao != undefined;
        if (valores?.aluno?.pessoa?.cpf != undefined) {
            valores.aluno.pessoa.cpf = Formatacoes.sanitizar("-/._", valores.aluno.pessoa.cpf!.trim());
        }

        if(estaEditando == false){
            let payload: TypeAluno = {
                ...valores.aluno,
                tipoDeDocumento: EnumTipoDeDocumento.RG,
                nacionalidades,
                responsaveis: valores.responsavel? [{ responsavel: valores.responsavel, aluno: {}, principal: true  }]: undefined
            }

            inserirAluno.mutate(payload);
        } else {
            let payload: TypeAluno = {
                ...valores.aluno,
                tipoDeDocumento: EnumTipoDeDocumento.RG,
                nacionalidades,
                responsaveis: valores.responsavel ? [{ 
                    responsavel: valores.responsavel, 
                    aluno: {},
                    principal: true,
                    ativo: true
                }] : undefined,
                ativo: !!props.alunoEmAlteracao?.ativo,
                codigo: props.alunoEmAlteracao?.codigo
            }

            atualizarAluno.mutate(payload)
        }
    }

    const cpfDoAlunoEmAlteracaoEstaVazio: boolean = (props.alunoEmAlteracao?.pessoa?.cpf ?? "").trim().length == 0;
    const rneDoAlunoEmAlteracaoEstaVazio: boolean = (props.alunoEmAlteracao?.pessoa?.rne ?? "").trim().length == 0;
    const passaporteDoAlunoEmAlteracaoEstaVazio: boolean = (props.alunoEmAlteracao?.pessoa?.passaporte ?? "").trim().length == 0;
    const rgDoAlunoEmAlteracaoEstaVazio: boolean = (valoresDoFormulario?.aluno?.documento ?? "").trim().length == 0;

    return (
        <React.Fragment>
            <Spin spinning={props.alunoEmAlteracao != undefined && configurouAsDependenciasDeAtualizacao == false}>
                <div>
                    {props.alunoEmAlteracao && props.privilegio == "responsavel" && (props.alunoEmAlteracao?.codigo ?? 0) > 0 && (
                        <VinculoNovoResponsavelModal aluno={props.alunoEmAlteracao} />
                    )}
                    <Form<IFormularioDeCadastroDeAluno>
                        form={form}
                        layout="vertical"
                        validateTrigger="onBlur"
                        autoComplete="off"
                        style={{
                            marginTop: 10,
                        }}
                        onFinish={
                            persistirDados
                                ? aoFinalizar
                                : (valores) => {
                                      const payload: TypeAluno = {
                                          ...valores.aluno,
                                          nacionalidades,
                                          responsaveis: valores.responsavel ? [{ responsavel: valores.responsavel, aluno: {}, principal: true }] : undefined,
                                      };

                                      if (payload.pessoa != null && (payload.pessoa?.cpf ?? null) != null) {
                                          payload.pessoa.cpf = Formatacoes.sanitizar("-", payload.pessoa.cpf?.trim() ?? "");
                                          payload.pessoa.cpf = Formatacoes.sanitizar("/", payload.pessoa.cpf?.trim() ?? "");
                                          payload.pessoa.cpf = Formatacoes.sanitizar(".", payload.pessoa.cpf?.trim() ?? "");
                                      }

                                      onFinish?.(payload);
                                  }
                        }
                        onFinishFailed={(info) => {
                            const mensagem = <>O formulário não pôde ser submetido.</>;
                            notification.open({
                                ...PropriedadesDeNotificacao.Erro(mensagem),
                            });
                        }}
                    >
                        <Row gutter={8}>
                             <Col xs={24} sm={12} md={7} lg={4} xl={4} xxl={3}>
                                <Form.Item<IFormularioDeCadastroDeAluno>
                                    name={["aluno", "pessoa", "codGenero"]}
                                    label="Gênero"
                                    rules={[{ required: props.privilegio == "responsavel", message: "Selecione o gênero do aluno!" }]}
                                >
                                    <PessoaGeneroSelect />
                                    {/* <SelectComPesquisa
                                        style={{ width: "100%" }}
                                        allowClear
                                        placeholder="Selecione o gênero"
                                        options={[
                                            { label: "Homem", value: EnumGenero.HOMEM },
                                            { label: "Mulher", value: EnumGenero.MULHER },
                                            { label: "Homem trans", value: EnumGenero.HOMEM_TRANS },
                                            { label: "Mulher trans", value: EnumGenero.MULHER_TRANS },
                                            { label: "Não binário", value: EnumGenero.NÃO_BINÁRIO },
                                        ]}
                                    /> */}
                                </Form.Item>
                            </Col>

                            {(generoForm !== undefined && generoForm === 7) && <Col xs={24} sm={12} md={7} lg={4} xl={4} xxl={3}>
                                <Form.Item<IFormularioDeCadastroDeAluno> name={["aluno", "pessoa", "descricaoGenero"]} label="Informe o gênero" rules={[{ max: 255 }]}>
                                    <Input placeholder="Descreva" />
                                </Form.Item>
                            </Col>}
                            
                            <Col xs={24} sm={12} md={10} lg={8} xl={7} xxl={6}>
                                <Form.Item<IFormularioDeCadastroDeAluno>
                                    name={["aluno", "pessoa", "nome"]}
                                    label="Nome completo de registro"
                                    rules={[{ required: true, max : 300 }]}
                                >
                                    <Input placeholder="Nome completo de registro" />
                                </Form.Item>
                            </Col>

                            {(generoForm !== undefined && generoForm > 0 && (generoForm !== 1 && generoForm !== 2)) &&<Col xs={24} sm={12} md={10} lg={8} xl={7} xxl={6}>
                                <Form.Item<IFormularioDeCadastroDeAluno> name={["aluno", "pessoa", "nomeSocial"]} label="Nome social" rules={[{ max: 300 }]}>
                                    <Input placeholder="Nome social do aluno" />
                                </Form.Item>
                            </Col>}
                        
                            <Col xs={24} sm={12} lg={4}>
                                <Form.Item<IFormularioDeCadastroDeAluno>
                                    name={["aluno", "dataDeNascimento"]}
                                    label="Data de nascimento"
                                    rules={[{ required: props.privilegio == "responsavel", message: "Por favor, informe a data de nascimento do aluno!" }]}
                                >
                                    <InputDate placeholder="DD/MM/YYYY" disabledDate={(date) => date.isAfter(new Date())} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12} lg={4}>
                                <Tooltip
                                    title={
                                        !estaAlterando || cpfDoAlunoEmAlteracaoEstaVazio || passaporteDoAlunoEmAlteracaoEstaVazio || rneDoAlunoEmAlteracaoEstaVazio ? undefined : (props.privilegio !== "administrador" ? "Contate um administrador caso deseje alterar este campo" : undefined) 
                                        // ((!cpfDoAlunoEmAlteracaoEstaVazio || !passaporteDoAlunoEmAlteracaoEstaVazio || !rneDoAlunoEmAlteracaoEstaVazio) || !estaAlterando) &&
                                        // props.privilegio !== "administrador"
                                        //     ? "Contate um administrador caso deseje alterar este campo"
                                        //     : undefined
                                    }
                                    placement="bottomLeft"
                                >
                                    <Form.Item<IFormularioDeCadastroDeAluno>
                                        label="O aluno possui"
                                        name={["meta", "tipoDeDocumentoPrincipal"]}
                                        rules={[{ required: true }]}
                                        initialValue={"cpf"}
                                    >
                                        <Select
                                            disabled={
                                                (!estaAlterando || cpfDoAlunoEmAlteracaoEstaVazio || passaporteDoAlunoEmAlteracaoEstaVazio || rneDoAlunoEmAlteracaoEstaVazio) ? false : props.privilegio !== "administrador"
                                            }
                                            placeholder="Selecione"
                                            options={[
                                                {
                                                    value: "cpf" as TipoDeDocumentoPrincipal,
                                                    label: "CPF",
                                                },
                                                {
                                                    value: "passaporte" as TipoDeDocumentoPrincipal,
                                                    label: "Passaporte",
                                                },
                                                {
                                                    value: "rne" as TipoDeDocumentoPrincipal,
                                                    label: "RNE",
                                                },
                                            ]}
                                        />
                                    </Form.Item>
                                </Tooltip>
                            </Col>

                            <Col xs={24} sm={12} lg={4}>
                                {valoresDoFormulario?.meta.tipoDeDocumentoPrincipal === "cpf" ? (
                                    <Tooltip
                                        title={
                                            !(cpfDoAlunoEmAlteracaoEstaVazio || !estaAlterando) && props.privilegio !== "administrador"
                                                ? "Contate um administrador caso deseje alterar este campo"
                                                : undefined
                                        }
                                    >
                                        <Form.Item<IFormularioDeCadastroDeAluno>
                                            name={["aluno", "pessoa", "cpf"]}
                                            label="CPF"
                                            rules={[
                                                { required: true, message: "Por favor, informe o CPF do aluno!" },
                                                { pattern: /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/, message: "Por favor, insira um CPF válido!" },
                                            ]}
                                        >
                                            <InputComReactIMask
                                                disabled={!(cpfDoAlunoEmAlteracaoEstaVazio || !estaAlterando) && props.privilegio !== "administrador"}
                                                maskOptions={{
                                                    mask: "000.000.000-00",
                                                    autofix: true
                                                }}
                                            />
                                        </Form.Item>
                                    </Tooltip>
                                ) : valoresDoFormulario?.meta.tipoDeDocumentoPrincipal === "rne" ? (
                                    <Tooltip
                                        title={
                                            !(rneDoAlunoEmAlteracaoEstaVazio || !estaAlterando) && props.privilegio !== "administrador"
                                                ? "Contate um administrador caso deseje alterar este campo"
                                                : undefined
                                        }
                                    >
                                        <Form.Item<IFormularioDeCadastroDeAluno>
                                            name={["aluno", "pessoa", "rne"]}
                                            label="RNE"
                                            rules={[{ required: true, message: "Por favor, informe o RNE do aluno!" }]}
                                        >
                                            <Input placeholder="RNE" disabled={!(rneDoAlunoEmAlteracaoEstaVazio || !estaAlterando) && props.privilegio !== "administrador"} />
                                        </Form.Item>
                                    </Tooltip>
                                ) : valoresDoFormulario?.meta.tipoDeDocumentoPrincipal === "passaporte" ? (
                                    <Tooltip
                                        title={
                                            !(passaporteDoAlunoEmAlteracaoEstaVazio || !estaAlterando) && props.privilegio !== "administrador"
                                                ? "Contate um administrador caso deseje alterar este campo"
                                                : undefined
                                        }
                                    >
                                        <Form.Item<IFormularioDeCadastroDeAluno>
                                            name={["aluno", "pessoa", "passaporte"]}
                                            label="Passaporte"
                                            rules={[{ required: true, message: "Por favor, informe o passaporte do aluno!" }]}
                                        >
                                            <Input
                                                placeholder="Passaporte"
                                                disabled={!(passaporteDoAlunoEmAlteracaoEstaVazio || !estaAlterando) && props.privilegio !== "administrador"}
                                            />
                                        </Form.Item>
                                    </Tooltip>
                                ) : undefined}
                            </Col>

                            <Col xs={24} sm={12} lg={6}>
                                <Form.Item<IFormularioDeCadastroDeAluno>
                                    /* Foi pedido que esse campo fosse fixo ao RG */

                                    name={["aluno", "documento"]}
                                    label={"RG"}
                                    rules={[{ message: "O campo não pode ser vazio!" }]}
                                >
                                    <Input placeholder={"Insira o RG"} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} lg={6}>
                                <Form.Item<IFormularioDeCadastroDeAluno>
                                    name={["aluno", "dataDeExpedicaoDoDocumento"]}
                                    label="Data de expedição"
                                    rules={[
                                        {
                                            required: rgDoAlunoEmAlteracaoEstaVazio === false,
                                            message: "Por favor, insira a data de expedição do documento!",
                                        },
                                    ]}
                                    dependencies={[["aluno", "tipoDeDocumento"]]}
                                >
                                    <InputDate
                                        style={{ width: "100%" }}
                                        placeholder="DD/MM/YYYY"
                                        disabled={rgDoAlunoEmAlteracaoEstaVazio === true}
                                        disabledDate={(date) => {
                                            if (date.isAfter(new Date())) return true;
                                            if (
                                                valoresDoFormulario?.aluno.dataDeNascimento != undefined &&
                                                date.isBefore(valoresDoFormulario?.aluno.dataDeNascimento)
                                            )
                                                return true;

                                            return false;
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {props.privilegio == "administrador" && (
                            <Row gutter={[16, 8]}>
                                <Col xs={24}>
                                    <Form.Item<IFormularioDeCadastroDeAluno>
                                        name={["responsavel", "codigo"]}
                                        label="Responsável principal"
                                        rules={[{ required: true, message: "Selecione o responsável do aluno" }]}
                                    >
                                        <SelectComPesquisa
                                            disabled={props.privilegio !== "administrador"}
                                            style={{ width: "100%" }}
                                            loading={responsaveisDisponiveis.isFetching}
                                            allowClear
                                            placeholder="Selecione o responsável"
                                            options={responsaveisDisponiveis.data?.data
                                                .sort((a, b) => Ordenar.String(a.pessoa?.nome + (a.pessoa?.cpf ?? ""), b.pessoa?.nome + (b.pessoa?.cpf ?? "")))
                                                .map((responsavel) => ({
                                                    value: responsavel.codigo,
                                                    label: `${responsavel.pessoa?.nome} (${
                                                        responsavel.pessoa?.cpf ? Formatacoes.formatarCPF(responsavel.pessoa?.cpf) : "N/A"
                                                    })`,
                                                }))}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                    </Form>

                    {props.privilegio == "administrador" && props.permitirCadastroDeResponsavel == true && (
                        <Row style={{ marginBottom: "1em" }}>
                            <Col xs={24}>
                                <Collapse
                                    accordion
                                    bordered={false}
                                    expandIconPosition="start"
                                    expandIcon={(panelProps) => {
                                        return (
                                            <React.Fragment>
                                                <Button
                                                    type="link"
                                                    style={{ padding: 0 }}
                                                    icon={
                                                        panelProps.isActive == undefined ? (
                                                            <></>
                                                        ) : panelProps.isActive == false ? (
                                                            <PlusOutlined />
                                                        ) : (
                                                            <DownOutlined />
                                                        )
                                                    }
                                                >
                                                    Novo responsável?
                                                </Button>
                                            </React.Fragment>
                                        );
                                    }}
                                    items={[
                                        {
                                            key: "1",
                                            children: (
                                                <CadastroDeResponsavel
                                                    privilegio="administrador"
                                                    txtSalvar="Salvar responsável"
                                                    txtAtualizar="Atualizar responsável"
                                                    depoisDeFinalizar={() => {
                                                        responsaveisDisponiveis.refetch();
                                                    }}
                                                />
                                            ),
                                        },
                                    ]}
                                />
                            </Col>
                        </Row>
                    )}
                    
                    {/* {props.privilegio == "responsavel" && (
                        <React.Fragment>
                            <Divider style={{ margin: 0 }} />
                            <Typography.Title level={5}>Nacionalidades</Typography.Title>
                            <FormularioDeNacionalidadesDoAluno nacionalidadesAPI={[nacionalidades, setNacionalidades]} />
                        </React.Fragment>
                    )} */}

                    {persistirDados && (
                        <>
                            <Divider />
                            <Row>
                                <Col xs={24}>
                                    <Flex align="center" justify="center" gap={16}>
                                        {props.aoCancelar && (
                                            <Button
                                                danger
                                                type="primary"
                                                key={"cancel"}
                                                onClick={() => {
                                                    props.aoCancelar && props.aoCancelar();
                                                }}
                                            >
                                                Cancelar
                                            </Button>
                                        )}

                                        <Button
                                            disabled={inserirAluno.isPending || atualizarAluno.isPending}
                                            onClick={form.submit}
                                            type="primary"
                                            key={"submit"}
                                        >
                                            {props.alunoEmAlteracao == undefined ? props.txtSalvar ?? "Salvar" : props.txtAtualizar ?? "Atualizar"}
                                        </Button>
                                    </Flex>
                                </Col>
                            </Row>
                        </>
                    )}
                </div>
            </Spin>
        </React.Fragment>
    );
}
